import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getToken, setToken } from '../lib/token';
import { refreshSession } from '../lib/refresh';
import {
  clearSessionUser, readSessionUser, requestPersistentStorage, saveSessionUser,
} from '../lib/offline/cache';
import { recordOnlineAuth } from '../lib/offline/profiles';
import { clearDek } from '../lib/offline/dekSession';

const TENANT: string = (import.meta.env.VITE_TENANT as string | undefined) ?? 'flightstandards';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  preferred_name: string | null;
  pilot_code: string | null;
  roles: string[];
  home_base_id: string | null; // matches the login/refresh payload (was a stale `home_base_icao`)
  home_route: string | null;   // user-settable home page (global nav); null = role default
  pref_theme: string;
  pref_dark_accent: string;
  pref_font_size: string;
  pref_time_entry: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  // 5d Phase 4: true when mount-time restore couldn't reach the server (offline) — the
  // session profile is encrypted, so it can't be read until the PIN unlock provides the
  // DEK. The offline-auth gate uses this to present the UNLOCK screen (not the login page)
  // when the device is enrolled, then calls adoptOfflineUser() with the decrypted profile.
  restoreUnavailable: boolean;
  adoptOfflineUser: (user: AuthUser) => void;
  // Merge a partial AuthUser into the live session after a self-service profile/email edit,
  // and write-through the cached offline profile so the top-bar identity stays consistent
  // without a reload. Only AuthUser-held fields apply (email/full_name/preferred_name/prefs);
  // fields the profile page holds but AuthUser doesn't (mobile, notif_*) are simply ignored.
  applyUserUpdate: (partial: Partial<AuthUser>) => void;
  // Returns the authenticated user so the caller can land them on their home route.
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthBody {
  data: { access_token: string; user: AuthUser; expires_in: number } | null;
  error: string | null;
}

// Defensive body read: an empty-bodied or non-JSON response (e.g. an empty 500 from a
// hung/wedged backend via the dev proxy) must NOT throw raw "Unexpected end of JSON input".
// Reads the body as text first; returns null when empty/unparseable so callers surface a
// clean error. A VALID JSON error envelope is preserved (its `error` is returned, not swallowed).
async function readAuthBody(res: Response): Promise<AuthBody | null> {
  let text = '';
  try { text = await res.text(); } catch { return null; }
  if (!text.trim()) return null;
  try { return JSON.parse(text) as AuthBody; } catch { return null; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [restoreUnavailable, setRestoreUnavailable] = useState(false);

  // On mount: try to restore the session from the httpOnly refresh token cookie.
  // This is the only network call — /refresh sets the new cookie + returns access_token + user.
  useEffect(() => {
    let cancelled = false;

    async function tryRestore() {
      try {
        // Single-flight refresh (see lib/refresh.ts): mount-time restore shares the SAME
        // in-flight /api/auth/refresh as any concurrent 401-driven refresh from apiFetch,
        // so a hard load fires ONE refresh, not one per fetch.
        const refreshed = await refreshSession();
        if (refreshed.ok) {
          setToken(refreshed.result.access_token);
          if (!cancelled) setUser(refreshed.result.user as AuthUser);
          // Write-through the last-known profile (NO tokens) + ask for durable storage,
          // same as a fresh login — a PWA reopened from the home screen never sees the
          // login page, so this is the common "logged in" moment on tablets.
          void saveSessionUser(refreshed.result.user);
          void requestPersistentStorage();
          // 5d Phase 4: a successful ONLINE auth re-stamps the uniform 7-day offline window
          // and clears any terminal lock on this user's profile (online re-auth restores).
          void recordOnlineAuth((refreshed.result.user as AuthUser | undefined)?.id);
          return;
        }
        if (refreshed.reason === 'rejected') {
          // Explicit 401/403 — the session is genuinely over. Clear everything.
          setToken(null);
          void clearSessionUser();
          return;
        }
        // 'unavailable' — network failure / unreachable backend (Step 5d Phase 1 rule:
        // no-network is NOT not-authenticated). 5d Phase 4: the session profile is now
        // ENCRYPTED, so it can't be read until the PIN unlock supplies the DEK. Flag the
        // offline-restore state; the offline-auth gate shows the UNLOCK screen for an
        // enrolled device and calls adoptOfflineUser() with the decrypted profile.
        // (readSessionUser here returns null without a DEK — that's expected.)
        setToken(null);
        setRestoreUnavailable(true);
        const cached = await readSessionUser(); // null unless already unlocked
        if (!cancelled && cached) setUser(cached as AuthUser);
      } catch {
        setToken(null);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    }

    tryRestore();
    return () => { cancelled = true; };
  }, []);

  // 5d Phase 4: the offline-auth gate calls this after a successful PIN unlock decrypts the
  // session profile — adopting the last-known user so the cached board renders offline.
  // No token is set (offline); the first online request heals via the silent-refresh path.
  const adoptOfflineUser = useCallback((u: AuthUser): void => {
    setUser(u);
    setRestoreUnavailable(false);
  }, []);

  const applyUserUpdate = useCallback((partial: Partial<AuthUser>): void => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...partial };
      void saveSessionUser(merged); // write-through (DEK-gated; no-op when locked)
      return merged;
    });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    let res: Response;
    try {
      res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Tenant': TENANT },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error('Could not reach the server. Please try again.');
    }

    // Defensive parse: an empty/non-JSON body (e.g. an empty 500 from a hung backend) is
    // surfaced as a clean retryable error, NOT raw "Unexpected end of JSON input". A valid
    // JSON error envelope still surfaces its own message.
    const body = await readAuthBody(res);
    if (!res.ok || !body || body.error || !body.data) {
      throw new Error(body?.error ?? (res.ok ? 'Login failed.' : 'Server error — please try again.'));
    }

    setToken(body.data.access_token);
    setUser(body.data.user);
    // Step 5d Phase 1: persist the last-known profile (NO tokens — token validity is
    // unchanged) for offline session restore, and request durable storage once.
    void saveSessionUser(body.data.user);
    void requestPersistentStorage();
    // 5d Phase 4: re-stamp the 7-day window + clear any terminal lock (online re-auth restores).
    void recordOnlineAuth(body.data.user.id);
    return body.data.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const token = getToken();
    // Clear local state immediately — don't wait for the network call. An explicit
    // sign-out also clears the persisted last-known profile (shared tablets must not
    // silently restore a signed-out user); the shared read cache itself stays, per the
    // 5d design (§7/R-8 — it matches what any enrolled user can see online).
    setToken(null);
    setUser(null);
    void clearSessionUser();
    clearDek(); // 5d Phase 4: drop the in-memory DEK on explicit sign-out (re-lock the cache)

    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant': TENANT,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
    } catch {
      // Best-effort — local state is already cleared.
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, isInitializing, restoreUnavailable, adoptOfflineUser, applyUserUpdate, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
